import { useInfiniteQuery } from '@tanstack/react-query'
import type { IProduct } from '@/app/[locale]/_types'
import productService from '@/app/[locale]/_service/product.service'
import { useDynamicLanguage } from '@/app/[locale]/_hooks/useShipper'
import { getProductQueryParams } from '../server-utils'

interface FilteredQueryResult {
  products: IProduct[]
  count: number
}

export function useFilteredProductsQuery(
  isFiltered: boolean,
  shipper_id: string | undefined,
  platform_id: string,
  queries: {
    menu_id?: string
    delivery_type: string
    tag_ids?: string
    sort?: string
    category_id?: string
    source_id?: string
  }
) {
  const { useDynamicLanguage: dynamicLanguageEnabled, isShipperResolved } =
    useDynamicLanguage()

  return useInfiniteQuery({
    queryKey: [
      'filtered-products',
      shipper_id,
      queries,
      dynamicLanguageEnabled,
    ],
    queryFn: async ({
      pageParam = 1,
    }): Promise<{
      products: IProduct[]
      count: number
      pageParam: number
    }> => {
      const params = await getProductQueryParams(pageParam, {
        tag_ids: queries.tag_ids,
        sort: queries.sort,
        category_id: queries.category_id,
        effectiveMenuId: queries.menu_id,
        deliveryType: queries.delivery_type,
        aggregator_id: queries.source_id,
      })
      const response = await productService.getAll(
        params,
        shipper_id,
        platform_id,
        dynamicLanguageEnabled
      )

      return {
        products: response?.data?.products || [],
        count: response?.data?.count || 0,
        pageParam,
      }
    },
    enabled:
      Boolean(shipper_id) && isShipperResolved && Boolean(isFiltered),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages): number | undefined => {
      if (!isFiltered) return undefined

      const loadedProductCount = allPages.reduce(
        (total, page) => total + page.products.length,
        0
      )

      return loadedProductCount < lastPage.count
        ? lastPage.pageParam + 1
        : undefined
    },
    select: (data): FilteredQueryResult => ({
      products: data.pages.flatMap((page) => page.products) as IProduct[],
      count: data.pages[0]?.count || 0,
    }),
    staleTime: 2 * 60 * 1000,
  })
}
